import type { QuoteSuggestion, InventoryState, SettlementReport, SprintState, RegimeState } from '../../types';
import type { MessageCallbacks } from './types';
import { normalizeTimestamp } from './types';

export function handleQuoteSuggestion(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'QUOTE_SUGGESTION' || obj?.type === 'quote_suggestion')) return false;

  const quoteObj = obj.quote || obj;
  const quoteData: QuoteSuggestion = {
    quote_id: quoteObj.quote_id || obj.quote_id || `quote_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    ticker: quoteObj.ticker || obj.ticker || quoteObj.symbol || obj.symbol || '',
    symbol: quoteObj.symbol || obj.symbol || quoteObj.ticker || obj.ticker || '',
    secId: obj.secId || obj.sec_id || quoteObj.secId || quoteObj.sec_id || '',
    mode: quoteObj.mode || obj.mode || 'manual',
    bid: {
      price: quoteObj.bid?.price ?? obj.bid?.price ?? null,
      qty: quoteObj.bid?.qty ?? quoteObj.bid?.quantity ?? obj.bid?.qty ?? obj.bid?.quantity ?? null,
    },
    ask: {
      price: quoteObj.ask?.price ?? obj.ask?.price ?? null,
      qty: quoteObj.ask?.qty ?? quoteObj.ask?.quantity ?? obj.ask?.qty ?? obj.ask?.quantity ?? null,
    },
    confidence: quoteObj.confidence ?? obj.confidence ?? 0.5,
    reason: quoteObj.reason || obj.reason || '',
    timestamp: quoteObj.timestamp || obj.timestamp || new Date().toISOString(),
    action: quoteObj.action || obj.action,
    spread_bps: quoteObj.spread_bps ?? obj.spread_bps,
    quote_decision: quoteObj.quote_decision || obj.quote_decision,
    features: quoteObj.features || obj.features,
  };

  cb.setQuoteSuggestions(prev => [quoteData, ...prev.filter(q => q.quote_id !== quoteData.quote_id)].slice(0, 10));
  return true;
}

export function handleInventoryUpdate(obj: any, cb: MessageCallbacks): boolean {
  if (!((obj?.kind === 'INVENTORY_UPDATE' || obj?.type === 'INVENTORY_UPDATE') && (obj.ticker || obj.data?.ticker))) return false;

  const payload = obj.data || obj;
  const ticker = (payload.ticker || obj.ticker || '').toUpperCase();
  if (!ticker) return true;

  const inv: InventoryState = {
    ticker,
    quantity: Number(payload.quantity ?? 0),
    settled_quantity: Number(payload.settled_quantity ?? 0),
    unsettled_buy_qty: Number(payload.unsettled_buy_qty ?? 0),
    unsettled_sell_qty: Number(payload.unsettled_sell_qty ?? 0),
    skew: Number(payload.skew ?? 0),
    utilization: Number(payload.utilization ?? 0),
    can_buy: payload.can_buy !== false,
    can_sell: payload.can_sell === true,
    avg_cost: Number(payload.avg_cost ?? 0),
    max_position: payload.max_position != null ? Number(payload.max_position) : undefined,
  };
  cb.setInventoryBySymbol(prev => new Map(prev).set(ticker, inv));
  return true;
}

export function handleSettlementUpdate(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'SETTLEMENT_UPDATE' || obj?.type === 'SETTLEMENT_UPDATE')) return false;

  const payload = obj.data || obj;
  const rawDates = Array.isArray(payload.settlement_dates)
    ? payload.settlement_dates
    : payload.settlement_schedule && typeof payload.settlement_schedule === 'object'
      ? Object.entries(payload.settlement_schedule).map(([date, v]) => ({ date, ...(v as Record<string, unknown>) }))
      : [];

  const settlement_dates: SettlementReport['settlement_dates'] = rawDates
    .map((sd: any) => {
      const date = sd.date ?? sd.settlement_date ?? '';
      const amount = Number(sd.amount ?? sd.net ?? 0);
      return {
        date,
        inflows: Number(sd.inflows ?? (amount > 0 ? amount : 0)),
        outflows: Number(sd.outflows ?? (amount < 0 ? Math.abs(amount) : 0)),
        net: Number(sd.net ?? amount),
      };
    })
    .sort((a: any, b: any) => a.date.localeCompare(b.date));

  const available = Number(payload.available_cash ?? 0);
  const reserved = Number(payload.reserved_cash ?? 0);
  const committed = Number(payload.committed_cash ?? 0);
  const receivables = Number(payload.receivables ?? 0);

  const normalizeByTicker = (raw: any): Record<string, number> | undefined => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
    const result: Record<string, number> = {};
    for (const [k, v] of Object.entries(raw)) {
      const key = (k || '').toUpperCase();
      if (key) result[key] = Number(v) || 0;
    }
    return Object.keys(result).length > 0 ? result : undefined;
  };

  cb.setSettlementReport({
    available_cash: available,
    reserved_cash: reserved,
    committed_cash: committed,
    receivables,
    tradeable_cash: Number(payload.tradeable_cash ?? (available - reserved - committed)),
    settlement_dates,
    as_of: payload.as_of ?? obj.as_of,
    mode: payload.mode || undefined,
    committed_by_ticker: normalizeByTicker(payload.committed_by_ticker ?? payload.committedByTicker ?? payload.by_ticker?.committed),
    receivables_by_ticker: normalizeByTicker(payload.receivables_by_ticker ?? payload.receivablesByTicker ?? payload.by_ticker?.receivables),
    reserved_by_ticker: normalizeByTicker(payload.reserved_by_ticker ?? payload.reservedByTicker),
    capital_by_ticker: normalizeByTicker(payload.capital_by_ticker ?? payload.capitalByTicker),
    tradeable_by_ticker: normalizeByTicker(payload.tradeable_by_ticker ?? payload.tradeableByTicker),
  });
  return true;
}

export function handlePerformanceUpdate(obj: any, cb: MessageCallbacks): boolean {
  const perfKind = String(obj?.kind ?? obj?.type ?? '').toUpperCase();
  if (!(perfKind === 'PERFORMANCE_UPDATE' || perfKind === 'PERFORMANCE_SNAPSHOT' || perfKind === 'EOD_REGIME')) return false;

  const envelope = obj.data || obj;
  const payload = (envelope?.performance && typeof envelope.performance === 'object') ? envelope.performance : envelope;
  const tickerPnl = payload?.ticker_pnl ?? payload?.tickerPnl;
  const derivedUnrealized = tickerPnl && typeof tickerPnl === 'object'
    ? Object.values(tickerPnl as Record<string, any>).reduce((s: number, info: any) => s + Number(info?.unrealized ?? 0), 0)
    : 0;
  const rawUnrealized = payload?.unrealized_pnl ?? payload?.unrealizedPnL;
  const unresolvedUnrealized = rawUnrealized != null ? Number(rawUnrealized) : derivedUnrealized;
  const unrealized = Number.isFinite(unresolvedUnrealized) ? unresolvedUnrealized : derivedUnrealized;

  cb.setPerformanceSnapshot({
    quotes_generated: Number(payload?.quotes_generated ?? payload?.quotesGenerated ?? 0),
    fills_processed: Number(payload?.fills_processed ?? payload?.fillsProcessed ?? 0),
    execution_rate: Number(payload?.execution_rate ?? payload?.executionRate ?? 0),
    avg_spread_capture_bps: Number(payload?.avg_spread_capture_bps ?? payload?.avgSpreadCaptureBps ?? 0),
    sharpe_ratio: payload?.sharpe_ratio != null ? Number(payload.sharpe_ratio) : (payload?.sharpeRatio != null ? Number(payload.sharpeRatio) : null),
    capital_utilization: Number(payload?.capital_utilization ?? payload?.capitalUtilization ?? 0),
    max_drawdown: Number(payload?.max_drawdown ?? payload?.maxDrawdown ?? 0),
    realized_pnl: Number(payload?.realized_pnl ?? payload?.realizedPnL ?? 0),
    unrealized_pnl: unrealized,
    as_of: payload?.as_of ?? payload?.asOf ?? obj.as_of ?? obj.timestamp,
  });

  if (tickerPnl && typeof tickerPnl === 'object') {
    const now = new Date().toISOString();
    const tickers = Object.entries(tickerPnl).map(([secId, info]: [string, any]) => {
      const realized = Number(info?.realized ?? 0);
      const unrealizedTicker = Number(info?.unrealized ?? 0);
      const totalPnl = realized + unrealizedTicker;
      const qty = Number(info?.quantity ?? 0);
      return {
        ticker: cb.labelForSecId(secId) || secId,
        sprint_score: totalPnl,
        roi_score: totalPnl,
        f1k_score: qty,
        capital_boost: totalPnl > 0 ? 1.1 : 1.0,
        is_winner: false,
        spike_detected: false,
      };
    }).sort((a, b) => b.sprint_score - a.sprint_score);
    if (tickers.length > 0) tickers[0].is_winner = true;
    cb.setSprintState({ sprint_start: now, sprint_end: now, tickers, winners: tickers.filter(t => t.is_winner).map(t => t.ticker) });
  }
  return true;
}

export function handleModelHealth(obj: any, cb: MessageCallbacks): boolean {
  const modelKind = String(obj?.kind ?? obj?.type ?? '').toUpperCase();
  if (!(['MODEL_HEALTH', 'MODEL_HEALTH_REPORT', 'MODEL_HEALTH_UPDATE', 'MODEL_METRICS'].includes(modelKind))) return false;

  const payload = obj.data || obj;
  const metrics = payload.metrics || payload.model_health || payload.modelHealth || payload.health || payload;

  cb.setModelHealth({
    fill_prob_accuracy: metrics.fill_prob_accuracy != null ? Number(metrics.fill_prob_accuracy) : (metrics.fillProbAccuracy != null ? Number(metrics.fillProbAccuracy) : undefined),
    fill_prob_mae: metrics.fill_prob_mae != null ? Number(metrics.fill_prob_mae) : (metrics.fillProbMae != null ? Number(metrics.fillProbMae) : undefined),
    wait_time_mae: metrics.wait_time_mae != null ? Number(metrics.wait_time_mae) : (metrics.waitTimeMae != null ? Number(metrics.waitTimeMae) : undefined),
    calibration_error: metrics.calibration_error != null ? Number(metrics.calibration_error) : (metrics.calibrationError != null ? Number(metrics.calibrationError) : undefined),
    predictions_count: metrics.predictions_count != null ? Number(metrics.predictions_count) : (metrics.predictionsCount != null ? Number(metrics.predictionsCount) : undefined),
    last_updated: metrics.timestamp_iso || metrics.timestamp || metrics.last_updated || payload.timestamp_iso || payload.timestamp || new Date().toISOString(),
    model_type: metrics.model_type || metrics.modelType || undefined,
    drift_detected: metrics.drift_detected ?? false,
  });

  if (metrics.drift_detected) {
    cb.pushActivity({
      id: `MODEL_UPDATE_drift_${Date.now()}_${cb.nextActivitySeq()}`,
      kind: 'MODEL_UPDATE',
      title: 'ML drift detected — model recalibrating',
      detail: `MAE: ${Number(metrics.fill_prob_mae ?? 0).toFixed(3)}`,
      timestamp: new Date().toISOString(),
      severity: 'warning',
    });
  }
  return true;
}

export function handleRiskLimit(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'RISK_LIMIT' && obj.limit_type)) return false;

  const ts = obj.timestamp ?? new Date().toISOString();
  const ev = {
    limit_type: obj.limit_type,
    ticker: obj.ticker,
    current_value: obj.current_value ?? 0,
    limit_value: obj.limit_value ?? 0,
    action_taken: obj.action_taken ?? '',
    timestamp: ts,
  };
  cb.setRiskEvents(prev => [ev, ...prev].slice(0, 20));
  cb.pushActivity({
    id: `RISK_LIMIT_${obj.ticker ?? ''}_${ts}_${cb.nextActivitySeq()}`,
    kind: 'RISK_LIMIT',
    ticker: obj.ticker,
    title: `${obj.limit_type} limit hit${obj.ticker ? `: ${obj.ticker}` : ''}`,
    detail: obj.action_taken || undefined,
    timestamp: ts,
    severity: 'critical',
    data: { current_value: ev.current_value, limit_value: ev.limit_value },
  });
  return true;
}

export function handleActivityEvent(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'ACTIVITY_EVENT' && obj.event_kind)) return false;

  cb.pushActivity({
    id: obj.id ?? `${obj.event_kind}_${Date.now()}_${cb.nextActivitySeq()}`,
    kind: obj.event_kind,
    ticker: obj.ticker,
    title: obj.title ?? obj.event_kind,
    detail: obj.detail,
    timestamp: obj.timestamp ?? new Date().toISOString(),
    severity: obj.severity ?? 'info',
    data: obj.data,
  });
  return true;
}

export function handleRegimeUpdate(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'REGIME_UPDATE' && obj.ticker && obj.regime)) return false;

  cb.setRegimeBySymbol(prev => {
    const current = prev.get(obj.ticker);
    const shifted = current && current.regime !== obj.regime;
    const next = new Map(prev);
    const rs: RegimeState = {
      ticker: obj.ticker,
      regime: obj.regime,
      confidence: obj.confidence ?? 1,
      last_changed: shifted ? (obj.timestamp ?? new Date().toISOString()) : (current?.last_changed ?? new Date().toISOString()),
      prev_regime: shifted ? current?.regime : current?.prev_regime,
      spread_multiplier: obj.spread_multiplier,
      size_multiplier: obj.size_multiplier,
    };
    next.set(obj.ticker, rs);
    if (shifted) {
      const ts = rs.last_changed;
      cb.pushActivity({
        id: `REGIME_SHIFT_${obj.ticker}_${ts}_${cb.nextActivitySeq()}`,
        kind: 'REGIME_SHIFT',
        ticker: obj.ticker,
        title: `${obj.ticker}: ${current?.regime ?? '?'} → ${obj.regime}`,
        detail: obj.confidence != null ? `Confidence: ${(obj.confidence * 100).toFixed(0)}%` : undefined,
        timestamp: ts,
        severity: ['VOLATILE', 'distribution'].includes(obj.regime) ? 'warning' : 'info',
        data: { from: current?.regime, to: obj.regime },
      });
    }
    return next;
  });
  return true;
}

export function handleSprintUpdate(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'SPRINT_UPDATE')) return false;

  const rows = (Array.isArray(obj.tickers) ? obj.tickers : []).map((t: any) => ({
    ...t,
    ticker: cb.labelForSecId(String(t?.ticker ?? '')) || String(t?.ticker ?? ''),
  }));
  const winners = (Array.isArray(obj.winners) ? obj.winners : []).map((w: any) => cb.labelForSecId(String(w)) || String(w));
  const sprintState: SprintState = { sprint_start: obj.sprint_start ?? '', sprint_end: obj.sprint_end ?? '', tickers: rows, winners };
  cb.setSprintState(sprintState);

  if (obj.announce_winners && winners.length > 0) {
    const ts = new Date().toISOString();
    for (const ticker of winners) {
      const tickerData = rows.find((t: SprintState['tickers'][0]) => t.ticker === ticker);
      cb.pushActivity({
        id: `SPRINT_WINNER_${ticker}_${ts}_${cb.nextActivitySeq()}`,
        kind: 'SPRINT_WINNER',
        ticker,
        title: `${ticker} wins sprint`,
        detail: tickerData ? `Score: ${tickerData.sprint_score.toFixed(2)} · Capital +50%` : 'Capital boost applied',
        timestamp: ts,
        severity: 'positive',
        data: tickerData as unknown as Record<string, unknown> | undefined,
      });
      if (tickerData?.spike_detected) {
        cb.pushActivity({
          id: `F1K_SPIKE_${ticker}_${ts}_${cb.nextActivitySeq()}`,
          kind: 'F1K_SPIKE',
          ticker,
          title: `${ticker} fill-rate spike`,
          detail: `F/1k: ${tickerData.f1k_score?.toFixed(2)}`,
          timestamp: ts,
          severity: 'positive',
          data: tickerData as unknown as Record<string, unknown> | undefined,
        });
      }
    }
  }
  return true;
}

export function handleModelUpdate(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'MODEL_UPDATE')) return false;

  const ts = obj.timestamp ?? new Date().toISOString();
  cb.pushActivity({
    id: `MODEL_UPDATE_${obj.model_name ?? ''}_${ts}_${cb.nextActivitySeq()}`,
    kind: 'MODEL_UPDATE',
    ticker: obj.ticker,
    title: `ML model updated${obj.model_name ? `: ${obj.model_name}` : ''}`,
    detail: obj.old_version && obj.new_version
      ? `v${obj.old_version} → v${obj.new_version}${obj.metrics?.accuracy_improvement != null ? ` · accuracy +${(obj.metrics.accuracy_improvement * 100).toFixed(1)}%` : ''}`
      : obj.samples_used != null ? `${obj.samples_used} samples` : undefined,
    timestamp: ts,
    severity: 'info',
    data: obj as Record<string, unknown>,
  });
  return true;
}

export function handleDailySummary(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'DAILY_SUMMARY')) return false;

  cb.setDailySummary({
    date: obj.date ?? new Date().toISOString().slice(0, 10),
    day_number: obj.day_number ?? 1,
    total_days: obj.total_days,
    realized_pnl: obj.realized_pnl ?? 0,
    unrealized_pnl: obj.unrealized_pnl ?? 0,
    cumulative_return_pct: obj.cumulative_return_pct ?? 0,
    day_drawdown_pct: obj.day_drawdown_pct ?? 0,
    trade_count: obj.trade_count ?? 0,
    buy_count: obj.buy_count ?? 0,
    sell_count: obj.sell_count ?? 0,
    quote_count: obj.quote_count ?? 0,
    execution_rate: obj.execution_rate ?? 0,
    two_sided_uptime_pct: obj.two_sided_uptime_pct ?? 0,
    avg_spread_bps: obj.avg_spread_bps ?? 0,
    win_rate_pct: obj.win_rate_pct ?? 0,
    top_performers: Array.isArray(obj.top_performers) ? obj.top_performers : [],
    worst_performer: obj.worst_performer,
    ticker_breakdown: Array.isArray(obj.ticker_breakdown) ? obj.ticker_breakdown : [],
  });
  return true;
}

export function handleMonthlySummary(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'MONTHLY_SUMMARY')) return false;

  cb.setMonthlySummary({
    month: obj.month ?? '',
    monthly_pnl: obj.monthly_pnl ?? 0,
    return_pct: obj.return_pct ?? 0,
    trading_days: obj.trading_days ?? 0,
    winning_days: obj.winning_days ?? 0,
    max_drawdown: obj.max_drawdown ?? 0,
    sharpe_ratio: obj.sharpe_ratio ?? 0,
    total_trades: obj.total_trades ?? 0,
    total_quotes: obj.total_quotes ?? 0,
    execution_rate: obj.execution_rate ?? 0,
    avg_spread_bps: obj.avg_spread_bps ?? 0,
    two_sided_uptime_pct: obj.two_sided_uptime_pct ?? 0,
    leaderboard: Array.isArray(obj.leaderboard) ? obj.leaderboard : [],
    mvp: obj.mvp ?? '',
    laggard: obj.laggard ?? '',
  });
  return true;
}
